import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import {
    formatPhone as formatSenegalPhone,
    constants,
    routes,
    getConnectUser,
    getStorage
} from 'helpers'
import { interactionOutcomeStyles } from 'components/interactions/constants'
import { FlagIcon, VisitedHandshakeIcon, NotVistedIcon } from '../../assets'
import HandShakeGIF from '../../assets/handshake.gif'
import './card.scss'
import { t } from 'i18next'
import { useBindDispatch } from '../../hooks'
import { findPermission } from '../../helpers/findPermission'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { InteractionOutcomes } from 'types'
import { findInteraction } from 'helpers/interactions/interactionHelper'
import { ClientDetailsCard } from 'components/client/ClientDetailsCard'
import { ClientPhone } from 'components/client/ClientPhone'
import { Modal } from 'components/modal/Modal'
import { ClientFullDetailsCard } from 'components/client/ClientFullDetailsCard'
import {
    getAgeProfile,
    getGenderAgeProfile
} from 'pages/village/helpers/villageClientHelpers'
import { colors } from 'styles/colors'
import { Interactions } from 'components/interactions/Interactions'

export const CardClientDetails = ({
    children,
    clientCode,
    clientPhone,
    otherClientData,
    clientPage = constants.CLIENT_TYPES.CURRENT_CLIENT,
    isClientDuplicate = false,
    clientStatus,
    interactionHandler,
    ...props
}) => {
    const { villageId } = useParams()
    const { interactions } = useSelector(
        ({ interactions }) => interactions[villageId] || {}
    )
    const lastVisitedClientCode = useSelector(
        (state) => state.lastVisitedClient
    )

    const [displayMore, setDisplayMore] = useState(false)
    const [clientInteractionStatus, setClientInteractionStatus] = useState()
    const [clientInteraction, setClientInteraction] = useState({})
    const [showVisitedGIF, setShowVisitedGIF] = useState(false)

    const navigate = useNavigate()
    const { pathname } = useLocation()

    const {
        UPDATE_CLIENT,
        USER_PERMISSIONS,
        CLIENT_DETAIL_LOCAL_STORE,
        CLIENT_FOR_ENROLLMENT_STORE,
        CLIENT_VISIT_INTERACTION
    } = constants
    const userPermissions = getStorage(USER_PERMISSIONS)
    const localClientData = JSON.parse(
        localStorage.getItem(CLIENT_DETAIL_LOCAL_STORE)
    )

    const canRecordInteractions = findPermission(
        userPermissions,
        constants.CAN_RECORD_INTERACTIONS
    )
    const canMakeCardPayment = findPermission(
        userPermissions,
        constants.CAN_MAKE_CARD_PAYMENT
    )

    const showInteractionsButton = !isClientDuplicate
    const storedClientVisitInteraction = getStorage(CLIENT_VISIT_INTERACTION)
    const veData = getConnectUser()
    const { clearLastVisitedClientHandler } = useBindDispatch()

    useEffect(() => {
        if (
            interactions?.length > 0 &&
            storedClientVisitInteraction?.client_code !== clientCode
        ) {
            const foundInteraction = findInteraction(interactions, clientCode)

            if (foundInteraction) {
                setClientInteraction(foundInteraction)
                localStorage.setItem(
                    constants.CLIENT_VISIT_INTERACTION,
                    JSON.stringify(foundInteraction)
                )
            }
        } else if (storedClientVisitInteraction?.client_code === clientCode) {
            setClientInteraction({ ...storedClientVisitInteraction })
        }
    }, [interactions?.length, clientCode])

    useEffect(() => {
        if (clientInteractionStatus?.className !== 'duplicate') {
            if (isClientDuplicate) {
                setClientInteractionStatus(
                    interactionOutcomeStyles[InteractionOutcomes.DUPLICATE]
                )
            } else {
                setClientInteractionStatus(
                    interactionOutcomeStyles[
                        clientInteraction?.outcome || 'default'
                    ]
                )
            }
        }
    }, [isClientDuplicate, clientInteraction])

    useEffect(() => {
        if (lastVisitedClientCode === clientCode) {
            clearLastVisitedClientHandler()
            if (!showVisitedGIF) {
                setShowVisitedGIF(true)
                setTimeout(() => {
                    setShowVisitedGIF(false)
                }, constants.VISITED_GIF_DURATION_IN_MS)
            }
        }
    }, [lastVisitedClientCode, clientCode])

    const formatPhone = formatSenegalPhone(clientPhone)

    const modifyClickHandler = () => {
        const { veVillages } = veData
        const { name: villageName } = veVillages.find(
            ({ sf_id: sfId }) => sfId === localClientData.village_id
        ) || { name: '' }
        localClientData.form_type = 'modification'
        localClientData.village_name = villageName
        localStorage.setItem(
            CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify(localClientData)
        )

        localStorage.setItem(
            CLIENT_FOR_ENROLLMENT_STORE,
            JSON.stringify({
                ...localClientData,
                modification: constants.CLIENT_MODIFICATION_TYPES.DETAILS
            })
        )
        return navigate(`/${routes.enroll}/${villageId}?from=${pathname}`)
    }
    const displayEditBtn =
        findPermission(userPermissions, UPDATE_CLIENT) && !isClientDuplicate

    const profileIcon = useMemo(
        () =>
            showVisitedGIF ? (
                <img
                    style={{ width: '70px', height: '70px' }}
                    // Add a timestamp to prevent caching and force reload of the GIF
                    src={`${HandShakeGIF}?t=${Date.now()}`}
                    alt="handshake-gif"
                />
            ) : clientPage === constants.CLIENT_TYPES.PREVIOUS_CLIENT ? (
                isClientDuplicate ? (
                    <FlagIcon width="50" height="50" color="red" />
                ) : clientInteractionStatus?.className === 'not_interested' ? (
                    <div
                        className="card--not-interested"
                        aria-label="not-interested-icon"
                    ></div>
                ) : clientInteractionStatus?.className === 'follow_up' ? (
                    <VisitedHandshakeIcon
                        color={colors.primary}
                        width="70"
                        height="70"
                    />
                ) : (
                    <NotVistedIcon />
                )
            ) : isClientDuplicate ? (
                <FlagIcon width="50" height="50" color="red" />
            ) : null,
        [showVisitedGIF, clientPage, isClientDuplicate, clientInteractionStatus]
    )

    const genderIconUrl = getGenderAgeProfile(
        t,
        otherClientData[t('gender')],
        otherClientData[t('age')]
    )

    const handleInteraction = (interactionPayload) => {
        interactionHandler(interactionPayload)

        const newInteraction = { ...interactionPayload }

        setClientInteraction(newInteraction)
        localStorage.setItem(constants.LAST_VISITED_CLIENT_CODE, clientCode)
        localStorage.setItem(
            constants.CLIENT_VISIT_INTERACTION,
            JSON.stringify(newInteraction)
        )
    }

    return (
        <div {...props}>
            <div
                className="card-client-detail--top"
                onClick={() => setDisplayMore(true)}
                aria-label="client-details-card"
            >
                <ClientDetailsCard
                    profileIcon={profileIcon}
                    genderIconUrl={genderIconUrl}
                    client={{
                        fullname: children,
                        client_code: clientCode,
                        gender: otherClientData[t('gender')],
                        ageGroup: getAgeProfile(otherClientData[t('age')])
                    }}
                />
            </div>
            <div className="card-client-detail--contact-interactions">
                <ClientPhone
                    clientPhoneInfo={{ formatPhone, clientPhone }}
                    permissions={{
                        isClientDuplicate
                    }}
                    modifyHandler={modifyClickHandler}
                />
            </div>
            {showInteractionsButton && (
                <Interactions
                    clientStatus={clientStatus}
                    clientInteraction={clientInteraction}
                    interactionHandler={handleInteraction}
                    canRecordInteractions={canRecordInteractions}
                    canMakeCardPayment={canMakeCardPayment}
                />
            )}
            <Modal
                showModal={displayMore}
                position={'bottom'}
                closeClickHandler={() => setDisplayMore(false)}
                showCloseIconBtn
                testId="client-full-details-modal"
            >
                <ClientFullDetailsCard
                    genderIconUrl={genderIconUrl}
                    client={{ fullname: children, client_code: clientCode }}
                    otherClientData={otherClientData}
                    btnTitle={t('modify')}
                    displayEditBtn={displayEditBtn}
                    modifyClickHandler={modifyClickHandler}
                />
            </Modal>
        </div>
    )
}

CardClientDetails.propTypes = {
    children: PropTypes.string,
    clientCode: PropTypes.string,
    clientPhone: PropTypes.string,
    otherClientData: PropTypes.object,
    preCertified: PropTypes.bool,
    clientPage: PropTypes.oneOf([
        constants.CLIENT_TYPES.CURRENT_CLIENT,
        constants.CLIENT_TYPES.PREVIOUS_CLIENT
    ]),
    isClientDuplicate: PropTypes.bool,
    clientStatus: PropTypes.string,
    interactionHandler: PropTypes.func
}
